/*
 * This file is part of the Akershus Eiendom Nettsted 2019 application.
 *
 * (c) APT AS
 *
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import React, { useRef, useState, useImperativeHandle, useEffect } from 'react';
import classnames from 'classnames';
import Form from 'components/Form/Form';
import PropTypes from 'prop-types';
import './Job.scss';

/**
 * This is the Job component.
 *
 * @author Thomas Sømoen <thomas@apt.no>
 *
 * @return {JSX}
 */
function Job({
  forwardedRef,
  id,
  className,
  title,
  form,
  route,
  error: errorMessage,
  onMount,
  onSubmit,
  ...props
}) {
  const formNode = useRef(null);
  const [error, setError] = useState(null);

  useEffect(onMount, []);

  useEffect(() => {
    if (route) {
      setError(null);
    }
  }, [route]);

  function validate() {
    const validForm = formNode.current.validate();
    if (!validForm) {
      return false;
    }

    if (!route) {
      setError('true');
      return false;
    }

    return true;
  }

  useImperativeHandle(forwardedRef, () => ({
    reset: formNode.current.reset,
    validate,
    getState: formNode.current.getState,
  }));

  function onFormSubmit(e) {
    if (validate()) {
      onSubmit(e);
    }
  }

  function renderTitle() {
    if (route === 'transactions') {
      return <h3 dangerouslySetInnerHTML={{ __html: title.transactions }} />;
    }

    if (route === 'rent') {
      return <h3 dangerouslySetInnerHTML={{ __html: title.rent }} />;
    }

    if (route === 'media') {
      return <h3 dangerouslySetInnerHTML={{ __html: title.media }} />;
    }

    return <h3 dangerouslySetInnerHTML={{ __html: title.default }} />;
  }

  function renderError() {
    if (!error) return null;

    return <div className="global-error">{errorMessage}</div>;
  }

  return (
    <div className={classnames('step step-job', className)}>
      {renderTitle()}
      <Form ref={formNode} {...form} {...props} onSubmit={onFormSubmit}>
        {renderError()}
      </Form>
    </div>
  );
}

/**
 * Declare expected prop types.
 *
 * @type {Object}
 */
Job.propTypes = {
  id: PropTypes.string,
  className: PropTypes.string,
  title: PropTypes.shape({
    default: PropTypes.string.isRequired,
    transactions: PropTypes.string.isRequired,
    rent: PropTypes.string.isRequired,
    media: PropTypes.string.isRequired,
  }).isRequired,
  form: PropTypes.object.isRequired,
  onMount: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
};

/**
 * Declare defaults for non-required props.
 *
 * @type {Object}
 */
Job.defaultProps = {
  title: {
    default: 'Default',
    transactions: 'Transactions',
    rent: 'Rent',
    media: 'Media',
  },
  form: {},
  onMount: () => {},
  onSubmit: () => {},
};

export default React.forwardRef((props, ref) => (
  <Job {...props} forwardedRef={ref} />
));
